import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery } from "@tanstack/react-query";
import { RefreshCw } from "lucide-react";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import {
  getProtocolConfigOptions,
  ouraMutationOptions,
} from "@/services/queryOptions/ouraQueryOptions";
import {
  protocolAmountSchema,
  protocolInitSchema,
  protocolUpdateSchema,
  protocolWithdrawSchema,
} from "@/types/oura";
import { TextField } from "../components/FormControls";
import { OperationsManager } from "./OperationsManager";

function solscan(signature?: string) {
  return signature ? `https://solscan.io/tx/${signature}` : undefined;
}

export function ProtocolManager() {
  const { toast } = useToast();
  const config = useQuery(getProtocolConfigOptions());
  const init = useMutation(ouraMutationOptions<z.infer<typeof protocolInitSchema>>("POST", "/api/v1/oura/protocol"));
  const update = useMutation(ouraMutationOptions<z.infer<typeof protocolUpdateSchema>>("PUT", "/api/v1/oura/protocol"));
  const deposit = useMutation(ouraMutationOptions<z.infer<typeof protocolAmountSchema>>("POST", "/api/v1/oura/protocol/deposit"));
  const withdraw = useMutation(ouraMutationOptions<z.infer<typeof protocolWithdrawSchema>>("POST", "/api/v1/oura/protocol/withdraw"));

  const initForm = useForm<z.infer<typeof protocolInitSchema>>({ resolver: zodResolver(protocolInitSchema) });
  const updateForm = useForm<z.infer<typeof protocolUpdateSchema>>({ resolver: zodResolver(protocolUpdateSchema) });
  const depositForm = useForm<z.infer<typeof protocolAmountSchema>>({ resolver: zodResolver(protocolAmountSchema) });
  const withdrawForm = useForm<z.infer<typeof protocolWithdrawSchema>>({ resolver: zodResolver(protocolWithdrawSchema) });

  const onSuccess = (signature: string) => {
    toast({
      title: "Transaction submitted",
      description: solscan(signature),
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold">Protocol</h2>
          <p className="text-sm text-zinc-400">Initialize, inspect, and move protocol funds.</p>
        </div>
        <Button variant="outline" size="icon" onClick={() => config.refetch()} aria-label="Refresh protocol">
          <RefreshCw className="h-4 w-4" />
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Current Configuration</CardTitle>
        </CardHeader>
        <CardContent className="grid gap-4 md:grid-cols-2">
          <div>
            <div className="text-xs text-zinc-500">Authority</div>
            <div className="break-all font-mono text-sm">{config.data?.authority ?? "Not loaded"}</div>
          </div>
          <div>
            <div className="text-xs text-zinc-500">Treasury</div>
            <div className="break-all font-mono text-sm">{config.data?.treasury ?? "Not loaded"}</div>
          </div>
        </CardContent>
      </Card>

      <div className="grid gap-4 xl:grid-cols-4">
        <Card>
          <CardHeader>
            <CardTitle>Initialize</CardTitle>
          </CardHeader>
          <CardContent>
            <form
              className="space-y-4"
              onSubmit={initForm.handleSubmit((values) =>
                init.mutate(values, { onSuccess: (result) => onSuccess(result.signature) }),
              )}
            >
              <TextField
                label="Treasury"
                placeholder="So11111111111111111111111111111111111111112"
                registration={initForm.register("treasury")}
                error={initForm.formState.errors.treasury}
              />
              <Button className="w-full" disabled={init.isPending}>Initialize</Button>
            </form>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Update Config</CardTitle>
          </CardHeader>
          <CardContent>
            <form
              className="space-y-4"
              onSubmit={updateForm.handleSubmit((values) =>
                update.mutate(values, { onSuccess: (result) => onSuccess(result.signature) }),
              )}
            >
              <TextField
                label="New authority"
                placeholder="So11111111111111111111111111111111111111112"
                registration={updateForm.register("new_authority_wallet")}
                error={updateForm.formState.errors.new_authority_wallet}
              />
              <TextField
                label="New treasury"
                placeholder="So11111111111111111111111111111111111111112"
                registration={updateForm.register("new_treasury_wallet")}
                error={updateForm.formState.errors.new_treasury_wallet}
              />
              <Button className="w-full" disabled={update.isPending}>Update</Button>
            </form>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Deposit</CardTitle>
          </CardHeader>
          <CardContent>
            <form
              className="space-y-4"
              onSubmit={depositForm.handleSubmit((values) =>
                deposit.mutate(values, { onSuccess: (result) => onSuccess(result.signature) }),
              )}
            >
              <TextField label="Amount" placeholder="1000000" registration={depositForm.register("amount")} error={depositForm.formState.errors.amount} />
              <Button className="w-full" disabled={deposit.isPending}>Deposit</Button>
            </form>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Withdraw</CardTitle>
          </CardHeader>
          <CardContent>
            <form
              className="space-y-4"
              onSubmit={withdrawForm.handleSubmit((values) =>
                withdraw.mutate(values, { onSuccess: (result) => onSuccess(result.signature) }),
              )}
            >
              <TextField
                label="Treasury"
                placeholder="So11111111111111111111111111111111111111112"
                registration={withdrawForm.register("treasury")}
                error={withdrawForm.formState.errors.treasury}
              />
              <TextField label="Amount" placeholder="1000000" registration={withdrawForm.register("amount")} error={withdrawForm.formState.errors.amount} />
              <Button className="w-full" disabled={withdraw.isPending}>Withdraw</Button>
            </form>
          </CardContent>
        </Card>
      </div>

      <OperationsManager resourceType="protocol" compact />
    </div>
  );
}
